import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { clone as cloneSkeleton } from 'three/examples/jsm/utils/SkeletonUtils.js';
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';

/**
 * Modelos de personaje de Poly Pizza (Quaternius, CC0). Ver procedencia y
 * licencia en `public/assets/polypizza/ATTRIBUTION.md`. Se cargan una sola vez
 * y se clonan por instancia para que cada uno tenga su propio esqueleto/mixer.
 *
 * Cuatro arquetipos visibles en escena (hombre, mujer, niño, niña). Como
 * Quaternius no publica modelos "kid" individuales con licencia CC0, niño y
 * niña reutilizan los esqueletos de hombre/mujer a una altura menor y con
 * una pose distinta: childlike proportionamento, mismo motor de animación.
 */
export type PlayerChoice = 'man' | 'woman' | 'boy' | 'girl';

export const PLAYER_CHOICES: ReadonlyArray<{ id: PlayerChoice; label: string; model: string; height: number }> = [
  { id: 'man',   label: 'Hombre',  model: '/assets/polypizza/adventurer.glb', height: 1.75 },
  { id: 'woman', label: 'Mujer',   model: '/assets/polypizza/woman.glb',       height: 1.65 },
  { id: 'boy',   label: 'Niño',    model: '/assets/polypizza/adventurer.glb', height: 1.20 },
  { id: 'girl',  label: 'Niña',    model: '/assets/polypizza/woman.glb',       height: 1.18 }
];

export const CHARACTER_MODELS = {
  /** Adulto masculino (protagonista por defecto). */
  man: '/assets/polypizza/adventurer.glb',
  /** Adulto femenino. */
  woman: '/assets/polypizza/woman.glb',
  /** Adulto femenino variante B (vestido distinto) para variedad por OA. */
  womanAlt: '/assets/polypizza/woman-b.glb',
  /** Niño (hombre reescalado a proporciones infantiles). */
  boy: '/assets/polypizza/adventurer.glb',
  /** Niña (mujer reescalada a proporciones infantiles). */
  girl: '/assets/polypizza/woman.glb',
  /** Pequeño robot volador conservado como drone de apoyo. */
  companion: '/assets/polypizza/robot.glb',
  enemy: '/assets/polypizza/slime.glb'
} as const;

/** Alturas objetivo en metros (se aplican al GLB tras clonar el esqueleto). */
export const CHARACTER_HEIGHTS = {
  man: 1.75,
  woman: 1.65,
  womanAlt: 1.65,
  boy: 1.20,
  girl: 1.18,
  companion: 1.15,
  enemy: 0.9
} as const;

/** Lee la elección de personaje guardada en localStorage; cae en 'man' si no hay. */
export function readPlayerChoice(): PlayerChoice {
  try {
    const v = localStorage.getItem('adventurengine.player');
    if (v === 'man' || v === 'woman' || v === 'boy' || v === 'girl') return v;
  } catch { /* localStorage puede estar deshabilitado */ }
  return 'man';
}

/** Persiste la elección de personaje para próximas sesiones. */
export function writePlayerChoice(c: PlayerChoice): void {
  try { localStorage.setItem('adventurengine.player', c); } catch { /* ignorar */ }
}

const loader = new GLTFLoader();
const cache = new Map<string, Promise<GLTF>>();

function loadGLTF(url: string): Promise<GLTF> {
  let p = cache.get(url);
  if (!p) {
    p = new Promise<GLTF>((resolve, reject) => loader.load(url, resolve, undefined, reject));
    cache.set(url, p);
  }
  return p;
}

/** Personaje con esqueleto animado: envuelve un GLTF clonado y su mixer. */
export class Character {
  readonly group = new THREE.Group();
  private readonly object: THREE.Object3D;
  private readonly mixer: THREE.AnimationMixer;
  private readonly actions = new Map<string, THREE.AnimationAction>();
  private current: THREE.AnimationAction | null = null;
  private currentName = '';

  private constructor(object: THREE.Object3D, gltf: GLTF, spin: number) {
    this.object = object;
    this.object.rotation.y = spin;
    this.object.traverse((o) => { o.castShadow = true; });
    this.group.add(this.object);
    this.mixer = new THREE.AnimationMixer(this.object);
    for (const clip of gltf.animations) this.actions.set(clip.name, this.mixer.clipAction(clip));
  }

  /**
   * Carga y clona un modelo, ajustando su escala para que mida `height` unidades
   * de alto (independiente de la escala nativa del GLB) y apoyándolo en y=0.
   */
  static async load(url: string, opts: { height?: number; spin?: number } = {}): Promise<Character> {
    const gltf = await loadGLTF(url);
    const object = cloneSkeleton(gltf.scene);
    object.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(object);
    const size = new THREE.Vector3(); box.getSize(size);
    const target = opts.height ?? Math.max(size.y, 0.001);
    const s = target / Math.max(size.y, 0.001);
    object.scale.setScalar(s);
    object.updateMatrixWorld(true);
    const grounded = new THREE.Box3().setFromObject(object);
    object.position.y -= grounded.min.y;
    const c = new Character(object, gltf, opts.spin ?? 0);
    return c;
  }

  /** Reproduce un clip por nombre exacto con fundido; no hace nada si ya suena. */
  play(name: string, fade = 0.2): void {
    if (this.currentName === name) return;
    const next = this.actions.get(name);
    if (!next) return;
    const prev = this.current;
    next.reset();
    next.setLoop(THREE.LoopRepeat, Infinity);
    next.fadeIn(fade);
    next.play();
    if (prev) prev.fadeOut(fade);
    this.current = next;
    this.currentName = name;
  }

  /** Reproduce un clip una sola vez (p. ej. ataque/muerte); devuelve el nombre. */
  once(name: string, fade = 0.1): boolean {
    const a = this.actions.get(name);
    if (!a) return false;
    a.reset();
    a.setLoop(THREE.LoopOnce, 1);
    a.clampWhenFinished = true;
    a.fadeIn(fade);
    a.play();
    if (this.current && this.current !== a) this.current.fadeOut(fade);
    this.current = a;
    this.currentName = name;
    return true;
  }

  has(name: string): boolean { return this.actions.has(name); }

  update(dt: number): void { this.mixer.update(dt); }

  dispose(): void {
    this.group.removeFromParent();
    this.mixer.stopAllAction();
  }
}
